import { useEffect, useRef } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { Button } from '@/shared/components/ui/button';
import { FormFieldInput } from '@/shared/components/custom/form-field/FormFieldInput';
import { FormFieldSelect } from '@/shared/components/custom/form-field/FormFieldSelect';
import {
  addWidgetFormSchema,
  getAvailableMetrics,
  typeSupportsAggregation,
  type AddWidgetFormInput,
} from '../../schemas';
import type { WidgetType, DashboardScopeType } from '../../types';
import { WidgetDataFields } from './WidgetDataFields';

const ALL_WIDTH_OPTIONS: Array<{
  value: AddWidgetFormInput['widthLabel'];
  label: string;
  cols: number;
}> = [
  { value: '1/4', label: 'sizes.width.1/4', cols: 3 },
  { value: '1/3', label: 'sizes.width.1/3', cols: 4 },
  { value: '1/2', label: 'sizes.width.1/2', cols: 6 },
  { value: '2/3', label: 'sizes.width.2/3', cols: 8 },
  { value: 'Completo', label: 'sizes.width.Completo', cols: 12 },
];

const TYPE_MIN_COLS: Partial<Record<WidgetType, number>> = {
  pie: 4,
  donut: 4,
  bar: 4,
  horizontal_bar: 4,
  stacked_bar: 6,
  line: 6,
  area: 6,
  table: 6,
};

const TYPE_DEFAULTS: Record<
  WidgetType,
  {
    widthLabel: AddWidgetFormInput['widthLabel'];
    heightLabel: AddWidgetFormInput['heightLabel'];
  }
> = {
  counter: { widthLabel: '1/4', heightLabel: 'Compacto' },
  pie: { widthLabel: '1/3', heightLabel: 'Normal' },
  donut: { widthLabel: '1/3', heightLabel: 'Normal' },
  bar: { widthLabel: '1/2', heightLabel: 'Normal' },
  horizontal_bar: { widthLabel: '1/2', heightLabel: 'Normal' },
  stacked_bar: { widthLabel: '1/2', heightLabel: 'Normal' },
  line: { widthLabel: 'Completo', heightLabel: 'Alto' },
  area: { widthLabel: 'Completo', heightLabel: 'Alto' },
  table: { widthLabel: 'Completo', heightLabel: 'Alto' },
};

const HEIGHT_OPTIONS: Array<{ value: AddWidgetFormInput['heightLabel']; label: string }> = [
  { value: 'Compacto', label: 'sizes.height.Compacto' },
  { value: 'Normal', label: 'sizes.height.Normal' },
  { value: 'Alto', label: 'sizes.height.Alto' },
  { value: 'Extra', label: 'sizes.height.Extra' },
];

const AGG_FUNCTIONS = ['count', 'sum', 'avg', 'min', 'max', 'percent'] as const;
const AGG_CONDITIONS = ['completed', 'archived', 'has_assignee'] as const;
const MAX_ITEMS = ['10', '20', '50', '100'] as const;

interface StepConfigProps {
  type: WidgetType;
  scopeType: DashboardScopeType;
  dashboardProjectId?: string;
  dashboardBoardId?: string;
  defaultValues?: Partial<AddWidgetFormInput>;
  isLoading: boolean;
  onSubmit: (data: AddWidgetFormInput) => void;
  onBack: () => void;
  isEdit?: boolean;
}

export function StepConfig({
  type,
  scopeType,
  dashboardProjectId,
  dashboardBoardId,
  defaultValues,
  isLoading,
  onSubmit,
  onBack,
  isEdit = false,
}: StepConfigProps) {
  const { t } = useTranslation('dashboard');

  const typeSizing = TYPE_DEFAULTS[type];
  const minCols = TYPE_MIN_COLS[type] ?? 0;
  const widthOptions = ALL_WIDTH_OPTIONS.filter((o) => o.cols >= minCols);
  const showAggregation = typeSupportsAggregation(type);

  const {
    control,
    handleSubmit,
    setValue,
    formState: { isDirty },
  } = useForm<AddWidgetFormInput>({
    resolver: zodResolver(addWidgetFormSchema(scopeType)),
    defaultValues: {
      title: '',
      metric: getAvailableMetrics(type, scopeType)[0] ?? '',
      widthLabel: typeSizing.widthLabel,
      heightLabel: typeSizing.heightLabel,
      aggregationFunction: 'count',
      aggregationCondition: 'completed',
      archivedOnly: false,
      // On board scope, pre-select the current board; edit values override this.
      ...(dashboardBoardId ? { scopeBoardId: dashboardBoardId } : {}),
      ...defaultValues,
      type,
    },
  });

  const aggregationFunction = useWatch({ control, name: 'aggregationFunction' });
  const metric = useWatch({ control, name: 'metric' });
  const pinnedProjectId = useWatch({ control, name: 'scopeProjectId' });

  // groupBy=Project is rejected (400 Widget.CrossScopeReference) both on
  // project/board dashboards and whenever the widget pins a project in its own
  // scope — so the option list depends on live form state, not just the route.
  const hasPinnedProject = scopeType !== 'tenant' || !!pinnedProjectId;
  const metrics = getAvailableMetrics(type, scopeType, hasPinnedProject);

  // The type selector lives outside this component and does not remount it, so
  // the type-derived fields are synced here — otherwise switching type after
  // opening the config step would keep building the previously selected widget.
  const prevTypeRef = useRef(type);
  useEffect(() => {
    if (prevTypeRef.current === type) return;
    prevTypeRef.current = type;
    setValue('type', type, { shouldDirty: true });
    setValue('widthLabel', TYPE_DEFAULTS[type].widthLabel);
    setValue('heightLabel', TYPE_DEFAULTS[type].heightLabel);
  }, [type, setValue]);

  // Keep groupBy inside the currently valid set (type change, or a project
  // pinned while grouping by project) instead of failing validation on submit.
  useEffect(() => {
    const available = getAvailableMetrics(type, scopeType, hasPinnedProject);
    if (available.length === 0) {
      if (metric) setValue('metric', '');
      return;
    }
    if (!available.includes(metric)) setValue('metric', available[0]);
  }, [type, scopeType, hasPinnedProject, metric, setValue]);

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-5 py-2">
      <div className="flex flex-col gap-5">
        {/* Title */}
        <FormFieldInput
          control={control}
          name="title"
          label={t('addWidget.form.title')}
          placeholder={t('addWidget.form.titlePlaceholder')}
          floatingLabel
          data-testid="dashboard-widget-title-input"
        />

        {/* Size */}
        <div className="grid grid-cols-2 gap-3">
          <FormFieldSelect
            control={control}
            name="widthLabel"
            label={t('addWidget.form.width')}
            floatingLabel
            options={widthOptions.map((o) => ({ value: o.value, label: t(o.label) }))}
            data-testid="dashboard-widget-width-select"
          />
          <FormFieldSelect
            control={control}
            name="heightLabel"
            label={t('addWidget.form.height')}
            floatingLabel
            options={HEIGHT_OPTIONS.map((o) => ({ value: o.value, label: t(o.label) }))}
            data-testid="dashboard-widget-height-select"
          />
        </div>

        {/* Group by — hidden for counter and table (no groupBy used) */}
        {metrics.length > 0 && (
          <FormFieldSelect
            control={control}
            name="metric"
            label={t('addWidget.form.metric')}
            floatingLabel
            options={metrics.map((m) => ({ value: m, label: t(`metrics.${m}`) }))}
            data-testid="dashboard-widget-metric-select"
          />
        )}

        {/* Aggregation — every shape except Table (fixed columns, nothing to compute) */}
        {showAggregation && (
          <>
            <FormFieldSelect
              control={control}
              name="aggregationFunction"
              label={t('addWidget.form.aggregation')}
              floatingLabel
              options={AGG_FUNCTIONS.map((f) => ({
                value: f,
                label: t(`aggregationFunctions.${f}`),
              }))}
              data-testid="dashboard-widget-aggregation-select"
            />
            {aggregationFunction === 'percent' && (
              <FormFieldSelect
                control={control}
                name="aggregationCondition"
                label={t('addWidget.form.condition')}
                floatingLabel
                options={AGG_CONDITIONS.map((c) => ({ value: c, label: t(`conditions.${c}`) }))}
                data-testid="dashboard-widget-condition-select"
              />
            )}
          </>
        )}

        {/* Bucket cap — only meaningful for the shapes that produce buckets/rows. */}
        {(metrics.length > 0 || type === 'table') && (
          <FormFieldSelect
            control={control}
            name="maxItems"
            label={t('addWidget.form.maxItems')}
            floatingLabel
            options={MAX_ITEMS.map((v) => ({ value: v, label: v }))}
            data-testid="dashboard-widget-max-items-select"
          />
        )}

        {/* Scope, date range and filters (optional) */}
        <WidgetDataFields
          control={control}
          setValue={setValue}
          scopeType={scopeType}
          dashboardProjectId={dashboardProjectId}
          dashboardBoardId={dashboardBoardId}
        />
      </div>

      {/* Footer */}
      <div className="flex justify-end gap-2 border-t pt-4">
        <Button
          type="button"
          variant="outline"
          onClick={onBack}
          data-testid="dashboard-widget-cancel-btn"
        >
          {t('addWidget.actions.cancel')}
        </Button>
        <Button
          type="submit"
          disabled={isLoading || !isDirty}
          data-testid="dashboard-widget-submit-btn"
        >
          {isLoading && (
            <span className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
          )}
          {isEdit ? t('addWidget.actions.save') : t('addWidget.actions.create')}
        </Button>
      </div>
    </form>
  );
}
